import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Buildings, ArrowRight } from "@phosphor-icons/react";

const JK = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

export default function CompleteGoogleProfile() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [company, setCompany] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const first = (user?.name || user?.email || "there").split(" ")[0];

  const submit = async (e) => {
    e.preventDefault();
    const name = company.trim();
    if (!name) {
      toast.error("Please enter your company name.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/auth/google/complete", { company: name });
      if (res.data?.user) {
        setUser(res.data.user);
        try { localStorage.setItem("user", JSON.stringify(res.data.user)); } catch { /* noop */ }
      }
      toast.success("Workspace created. Welcome to RecoverOS!");
      navigate("/welcome");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create your workspace. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="complete-google-profile-page">
      <header className="flex items-center justify-between px-6 lg:px-10 h-16 border-b border-slate-200 bg-white">
        <Brand iconSize={30} textClass="text-lg" style={JK} />
        <button onClick={logout} className="text-sm font-semibold text-slate-500 hover:text-slate-900" data-testid="complete-profile-signout">Sign out</button>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
            <div className="h-12 w-12 rounded-xl bg-[#0B3D91]/10 flex items-center justify-center">
              <Buildings size={26} weight="fill" className="text-[#0B3D91]" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mt-5" style={JK}>Complete your RecoverOS account</h1>
            <p className="text-sm text-slate-500 mt-2">
              Hi {first}, you're signed in as <strong className="text-slate-700">{user?.email}</strong>. Tell us your
              company name and we'll set up your workspace.
            </p>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="company" className="text-sm font-semibold text-slate-700">Company name</Label>
                <Input
                  id="company"
                  data-testid="complete-profile-company-input"
                  placeholder="e.g. Acme Finance Pvt Ltd"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  autoFocus
                  disabled={submitting}
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#0B3D91] hover:bg-[#0a3479] text-white font-semibold h-11"
                disabled={submitting || !company.trim()}
                data-testid="complete-profile-submit"
              >
                {submitting ? "Creating your workspace…" : (<span className="flex items-center justify-center gap-2">Create workspace <ArrowRight size={18} weight="bold" /></span>)}
              </Button>
            </form>
          </div>
          <p className="text-center text-xs text-slate-400 mt-4">You can invite your team and import your portfolio next.</p>
        </div>
      </main>
    </div>
  );
}
